import { useState, useEffect, useMemo, useContext } from 'react';
import {
  AgxColumn,
  AgxLabel,
  AgxRadio,
  AgxCheckbox,
  AgxSelect,
  Images,
  AgxBodyText,
} from '@urbanx/agx-ui-components';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { AuthContext } from '@urbanx/agx.auth.client';
import { User } from 'types/Auth';
import { fetchListingAgents } from '../../listingAgent/listingAgentReducer';
import { LoadingState } from 'utils/loadingState';
import { isEqual } from 'lodash-es';
import { ContactInfo, DocuSignInfo, SendForDocuSigningType } from './types';
import './SendForDocuSigning.scss';

const constructAgencyContact = (userAccount?: User): ContactInfo => {
  return {
    name: {
      firstName: userAccount?.firstName,
      lastName: userAccount?.lastName,
    },
    email: {
      address: userAccount?.agent ?? '',
    },
  };
};

interface SendForDocuSigningProps {
  id: string;
  label?: string;
  onValueChanged: (value: { id: string; value: DocuSignInfo }) => void;
  defaultValue?: DocuSignInfo;
  validate?: boolean;
}

const SendForDocuSigning = ({
  id,
  label,
  onValueChanged,
  defaultValue,
  validate,
}: SendForDocuSigningProps) => {
  const { userAccount, getAuthToken } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const [localError, setLocalError] = useState('');
  const { agents, loadingState: agentsLoadingState } = useAppSelector(
    state => state.listingAgent
  );

  const [sendForDocuSigningState, setSendForDocuSigningState] =
    useState<DocuSignInfo>({
      condition: !!defaultValue?.agencyContact
        ? SendForDocuSigningType.EmailToAgent
        : defaultValue?.hasOwnProperty('sendToBuyers')
          ? SendForDocuSigningType.SendForDocuSigning
          : undefined,

      sendToBuyers: defaultValue?.sendToBuyers ?? false,
      sendToVendors: defaultValue?.sendToVendors ?? false,
      transferDocuSignOwnershipToAgent:
        defaultValue?.transferDocuSignOwnershipToAgent ?? false,
      agencyContact: constructAgencyContact(userAccount),
      agentEmail: {
        address: defaultValue?.agentEmail?.address ?? '',
      },
    } as DocuSignInfo);

  const {
    condition,
    sendToBuyers,
    sendToVendors,
    transferDocuSignOwnershipToAgent,
    agencyContact,
    agentEmail,
  } = sendForDocuSigningState;

  useEffect(() => {
    const updatedAgencyContact = constructAgencyContact(userAccount);

    if (isEqual(updatedAgencyContact, sendForDocuSigningState.agencyContact))
      return;

    setSendForDocuSigningState({
      ...sendForDocuSigningState,
      agencyContact: updatedAgencyContact ?? {
        name: {},
        email: { address: '' },
      },
    });
  }, [userAccount, setSendForDocuSigningState, sendForDocuSigningState]);

  const validatePage = (state: DocuSignInfo) => {
    if (validate) {
      if (!state.condition) {
        setLocalError('Select an option');
        return;
      }

      if (
        state.condition === SendForDocuSigningType.SendForDocuSigning &&
        !state.sendToBuyers &&
        !state.sendToVendors
      ) {
        setLocalError('Select at least one option');
        return;
      }

      setLocalError('');
    }
  };

  useEffect(() => {
    if (agentsLoadingState !== LoadingState.NotLoaded) return;

    getAuthToken().then(token => {
      if (!token) return;
      dispatch(fetchListingAgents(token));
    });
  }, [agentsLoadingState]);

  const agentOptions = [
    {
      value: '',
      label: 'Please select an agent',
    },
  ].concat(
    agents.map(agent => {
      return {
        value: agent.id,
        label: `${agent.name?.firstName} ${agent.name?.lastName}`,
      };
    })
  );

  useEffect(() => {
    condition === SendForDocuSigningType.SendForDocuSigning &&
    (sendToBuyers || sendToVendors || transferDocuSignOwnershipToAgent)
      ? onValueChanged({
          id,
          value: {
            agencyContact,
            sendToBuyers,
            sendToVendors,
            transferDocuSignOwnershipToAgent,
            condition,
          },
        })
      : onValueChanged({
          id,
          value: {
            agencyContact,
            condition,
            agentEmail,
            sendToBuyers,
            sendToVendors,
            transferDocuSignOwnershipToAgent,
          },
        });
  }, [sendForDocuSigningState]);

  useEffect(() => {
    validatePage(sendForDocuSigningState);
  }, [validate]);

  const selectDefault = useMemo(() => {
    return agentOptions.find(opt => opt.value === agentEmail?.address);
  }, [agentEmail, agentOptions]);

  return (
    <div className="agxRadio">
      <AgxLabel medium>{label}</AgxLabel>
      <div className="agxRadio-group">
        <AgxRadio
          id="sendForDocuSigning"
          label="Send for Docusigning"
          onCheckChanged={() => {
            setSendForDocuSigningState(prev => ({
              ...prev,
              condition: SendForDocuSigningType.SendForDocuSigning,
            }));
            validatePage({
              ...sendForDocuSigningState,
              condition: SendForDocuSigningType.SendForDocuSigning,
            });
          }}
          checked={condition === SendForDocuSigningType.SendForDocuSigning}
          subContent={
            condition === SendForDocuSigningType.SendForDocuSigning
              ? [
                  <AgxColumn largeGap fill key="sendForContent">
                    <AgxColumn mediumGap>
                      <AgxLabel medium>Send the contract to:</AgxLabel>
                      <AgxCheckbox
                        id="sendTheContractTo_Buyers"
                        defaultValue={sendToBuyers}
                        subLabel="Send to Buyer(s)"
                        onValueChanged={({ value }) => {
                          setSendForDocuSigningState(prev => ({
                            ...prev,
                            sendToBuyers: value,
                          }));
                          validatePage({
                            ...sendForDocuSigningState,
                            sendToBuyers: value,
                          });
                        }}
                      />
                      <AgxCheckbox
                        id="sendTheContractTo_vendors"
                        defaultValue={sendToVendors}
                        subLabel="Send to Vendors"
                        onValueChanged={({ value }) => {
                          setSendForDocuSigningState(prev => ({
                            ...prev,
                            sendToVendors: value,
                          }));
                          validatePage({
                            ...sendForDocuSigningState,
                            sendToVendors: value,
                          });
                        }}
                      />
                      {localError && validate && (
                        <div className={`errorMessage`}>
                          <Images.AlertCircle />
                          <AgxBodyText small extraClasses={'error'}>
                            {localError}
                          </AgxBodyText>
                        </div>
                      )}
                    </AgxColumn>
                    <AgxColumn mediumGap>
                      <AgxLabel medium>
                        Do you want ownership of the Docusign Envelope
                        transferred to you?
                      </AgxLabel>
                      <AgxCheckbox
                        id="transferOwnershipToMe"
                        defaultValue={transferDocuSignOwnershipToAgent}
                        subLabel="Yes, transfer ownership to me"
                        onValueChanged={({ value }) =>
                          setSendForDocuSigningState(prev => ({
                            ...prev,
                            transferDocuSignOwnershipToAgent: value,
                          }))
                        }
                      />
                    </AgxColumn>
                  </AgxColumn>,
                ]
              : undefined
          }
        />
        <AgxRadio
          id="emailToAgent"
          label="Email to agent"
          onCheckChanged={() => {
            setSendForDocuSigningState(prev => ({
              ...prev,
              condition: SendForDocuSigningType.EmailToAgent,
              agentEmail: {
                address: userAccount?.agent ?? '',
              },
            }));
            validatePage({
              ...sendForDocuSigningState,
              condition: SendForDocuSigningType.EmailToAgent,
            });
          }}
          checked={condition === SendForDocuSigningType.EmailToAgent}
          subContent={
            condition === SendForDocuSigningType.EmailToAgent
              ? [
                  <AgxSelect
                    id={id}
                    key={id}
                    options={agentOptions}
                    label="Agent to email contract to:"
                    onValueChanged={({ value }) => {
                      setSendForDocuSigningState(prev => ({
                        ...prev,
                        agentEmail: {
                          address: value,
                        },
                      }));
                    }}
                    defaultValue={selectDefault}
                    required
                  />,
                ]
              : undefined
          }
        />
      </div>
      {localError && validate && !sendForDocuSigningState.condition && (
        <div className={`errorMessage`}>
          <Images.AlertCircle />
          <AgxBodyText small extraClasses={'error'}>
            {localError}
          </AgxBodyText>
        </div>
      )}
    </div>
  );
};

export default SendForDocuSigning;
